// The medieval style's band (docs/AUDIO_DESIGN.md §12): a bowed viol for the bass, a recorder (or a wooden flute) with
// grace-note ornaments in the track's mode, and hand drums (frame drum, slap, tambourine, finger taps, a bodhrán tone).
// Persistent voices gated with setTargetAtTime, like instruments.js: nothing is allocated per note or hit.
import { clamp } from '../../engine/core.js';
import { mtof, hold, noise } from '../dsp.js';
import { LEVELS, gain, filter, osc, panner, persistent } from '../instruments.js';

// Levels (tuning knobs for the mix balance), against the jazzhop bass, lead and kit they replace.
export const MED = { bass: 0.55, lead: 2.1, kit: 1.05 };
const mod12 = n => ((n % 12) + 12) % 12;

/* ---------------- viol: a bowed bass (saw + sine through a warm LP and a wooden body) ---------------- */
/** saw f + sine f (.6) → LP p.lp (700–1000 Hz, Q .7) → body 280 Hz (+3 dB, Q 1.2) → amp. The bow: attack τ 45 ms to 0.42v,
 *  settling to .8 of it (τ .3 s), release τ 90 ms; a note on a still-sounding one changes bow (a dip to .35, 12 ms);
 *  vibrato 5.2 Hz ±5 ¢ from 350 ms. The saw's 2nd–8th harmonics carry it on phones. p: { lp } */
export function createViol(ac, out, p, { registry, tag, t0 = ac.currentTime }) {
  const saw = osc(ac, 'sawtooth', 55), sine = osc(ac, 'sine', 55), sG = gain(ac, 0.6), lp = filter(ac, 'lowpass', p.lp || 850, 0.7);
  const body = filter(ac, 'peaking', 280, 1.2, 3), amp = gain(ac, 0), vib = osc(ac, 'sine', 5.2), vibG = gain(ac, 0);
  saw.connect(lp); sine.connect(sG); sG.connect(lp); lp.connect(body); body.connect(amp); amp.connect(out);
  vib.connect(vibG); vibG.connect(saw.detune); vibG.connect(sine.detune);
  const v = persistent(registry, tag, [saw, sine, vib], [saw, sine, sG, lp, body, amp, vib, vibG], t0);
  let off = -1;
  return {
    note(t, midi, vel, dur, slide = false) {
      const f = mtof(midi), A = 0.42 * vel * MED.bass, d = Math.max(0.1, dur);
      for (const prm of [saw.frequency, sine.frequency, amp.gain, vibG.gain]) hold(prm, t);
      for (const o of [saw, sine]) {
        if (slide) { o.frequency.setValueAtTime(f * Math.pow(2, -1 / 12), t); o.frequency.setTargetAtTime(f, t, 0.03); }
        else o.frequency.setValueAtTime(f, t);
      }
      if (t < off) { amp.gain.setTargetAtTime(0.35 * A, t, 0.012); amp.gain.setTargetAtTime(A, t + 0.03, 0.045); }   // bow change
      else amp.gain.setTargetAtTime(A, t, 0.045);
      amp.gain.setTargetAtTime(0.8 * A, t + 0.15, 0.3);
      amp.gain.setTargetAtTime(0, t + d, 0.09);
      vibG.gain.setTargetAtTime(0, t, 0.03);
      if (d > 0.5) vibG.gain.setTargetAtTime(5, t + 0.35, 0.2);
      off = t + d;
    },
    dispose(t) { v.dispose(t); },
  };
}

/* ---------------- recorder / wooden flute with ornaments in the mode ---------------- */
export const MODES = {
  dorian: [0, 2, 3, 5, 7, 9, 10], aeolian: [0, 2, 3, 5, 7, 8, 10], mixolydian: [0, 2, 4, 5, 7, 9, 10], ionian: [0, 2, 4, 5, 7, 9, 11],
};
//            triangle share · sustained breath · chiff · attack τ · LP · breath BP (× f) · vibrato Hz, ±¢ · amp flutter
const REC = { tri: 0.22, breath: 0.25, chiff: 1, atk: 0.018, lp: 3000, bp: 2.6, vib: [5, 9], trem: 0.05 };
const WOOD = { tri: 0.12, breath: 0.4, chiff: 0.6, atk: 0.034, lp: 2300, bp: 2.2, vib: [4.5, 12], trem: 0.07 };

/**
 * createRecorder(ac, out, send, plan, opts) → { note, dispose }   plan.lead.timbre 'recorder' | 'wood'; plan.medieval: { mode, orn }
 * sine f + triangle f (.22 recorder / .12 wood: hollow odd harmonics) → LP 3 / 2.3 kHz; breath = noise → BP 2.6·f (Q .9,
 * wood 2.2·f) inside the note's envelope: a chiff of 1 for ~30 ms on a tongued note, then .25 (the held breath ≈ 35 dB under
 * the tone; wood .6 / .4, ≈ 32 dB). Tongued: a 12 ¢ scoop up (τ 25 ms) and a dip to .2 on a still-sounding note, attack
 * τ 18 ms (wood 34); slurred (glide): the pitch moves in τ 12 ms, no chiff. Vibrato
 * 5 Hz ±9 ¢ (wood 4.5 Hz ±12) with a ±5 % breath flutter, only from 300 ms into notes longer than 0.4 s. Release τ 70 ms.
 * Ornaments, before notes ≥ 0.18 s, in the track's mode (MODES): a passing note when the melody moves a 3rd within
 * 0.35 s (p .5, 50–85 ms), else with p orn (.15–.36) a cut (the upper neighbour, 62 %) or a tap (the lower), 40–65 ms.
 * Dotted-8th echo (3 steps, fb .25, LP 2.2k, wet .2), pan lead.pan, reverb send .5.
 */
export function createRecorder(ac, out, send, plan, { registry, tag, stepDur, r, t0 = ac.currentTime }) {
  const lead = plan.lead, M = plan.medieval, T = lead.timbre === 'wood' ? WOOD : REC, tonic = plan.key.tonic;
  const scale = MODES[M.mode] || MODES.dorian, inMode = m => scale.includes(mod12(m - tonic));
  const nodes = [], N = x => (nodes.push(x), x);
  const o1 = N(osc(ac, 'sine', 440)), o2 = N(osc(ac, 'triangle', 440)), g2 = N(gain(ac, T.tri));
  const src = N(ac.createBufferSource()), bp = N(filter(ac, 'bandpass', 2400, 0.9)), breath = N(gain(ac, 0));
  src.buffer = noise(ac, 'white'); src.loop = true;
  const lp = N(filter(ac, 'lowpass', T.lp, 0.5)), amp = N(gain(ac, 0)), trem = N(gain(ac, 1)), pan = N(panner(ac, lead.pan || 0)), sendG = N(gain(ac, 0.5));
  const dly = N(ac.createDelay(2)), dLP = N(filter(ac, 'lowpass', 2200, 0.5)), fb = N(gain(ac, 0.25)), wet = N(gain(ac, 0.2));
  const vib = N(osc(ac, 'sine', T.vib[0])), vibG = N(gain(ac, 0)), tremG = N(gain(ac, 0));
  dly.delayTime.value = Math.min(1.9, 3 * stepDur);
  o1.connect(amp); o2.connect(g2); g2.connect(amp); src.connect(bp); bp.connect(breath); breath.connect(amp);
  amp.connect(lp); lp.connect(trem); trem.connect(pan); trem.connect(dly); dly.connect(dLP); dLP.connect(fb); fb.connect(dly); dLP.connect(wet); wet.connect(pan);
  pan.connect(out); pan.connect(sendG); sendG.connect(send);
  vib.connect(vibG); vibG.connect(o1.detune); vibG.connect(o2.detune); vib.connect(tremG); tremG.connect(trem.gain);
  const p = persistent(registry, tag, [o1, o2, src, vib], nodes, t0, 1.3);
  let last = null, off = -1;
  const setF = (hz, at, tau) => { for (const o of [o1, o2]) { if (tau) o.frequency.setTargetAtTime(hz, at, tau); else o.frequency.setValueAtTime(hz, at); } };
  return {
    note(t, midi, vel, dur, glide = false) {
      const d = Math.max(0.06, dur), f = mtof(midi), A = 0.2 * vel * MED.lead, prev = last;
      last = { midi, end: t + d };
      const g = graceNote(prev, t, midi, d, r, M.orn, inMode), grace = g ? g.m : null, gd = g ? g.gd : 0;
      for (const prm of [o1.frequency, o2.frequency, amp.gain, breath.gain, vibG.gain, tremG.gain]) hold(prm, t);
      if (grace != null) { setF(mtof(grace), t, glide ? 0.008 : 0); setF(f, t + gd, 0.006); }
      else if (glide) setF(f, t, 0.012);
      else { setF(f * Math.pow(2, -12 / 1200), t, 0); setF(f, t, 0.025); }
      bp.frequency.setValueAtTime(clamp(T.bp * f, 1200, 4800), t);
      if (glide) amp.gain.setTargetAtTime(A, t, 0.03);
      else {
        if (t < off) amp.gain.setTargetAtTime(0.2 * A, t, 0.005);     // the tongue stops the air for a moment
        amp.gain.setTargetAtTime(A, t + (t < off ? 0.012 : 0), T.atk);
        breath.gain.setTargetAtTime(T.chiff, t, 0.003);
      }
      breath.gain.setTargetAtTime(T.breath, t + 0.03, 0.04);
      amp.gain.setTargetAtTime(0, t + d, 0.07);
      breath.gain.setTargetAtTime(0, t + d, 0.06);
      vibG.gain.setTargetAtTime(0, t, 0.03); tremG.gain.setTargetAtTime(0, t, 0.03);
      if (d > 0.4) { vibG.gain.setTargetAtTime(T.vib[1], t + 0.3, 0.18); tremG.gain.setTargetAtTime(T.trem, t + 0.3, 0.18); }
      off = t + d;
    },
    dispose(t) { p.dispose(t); },
  };
}

/** The grace note before a melody note of d s at t, or null: a passing note of the mode when the melody moves a 3rd within
 *  0.35 s of the last note (p .5, 50–85 ms), else with p orn a cut (the mode's upper neighbour, 62 %) or a tap (the lower),
 *  40–65 ms; only before notes of at least 0.18 s. prev: { midi, end } | null. → { m, gd } | null */
export function graceNote(prev, t, midi, d, r, orn, inMode) {
  if (d < 0.18) return null;
  const iv = prev ? Math.abs(midi - prev.midi) : 0;
  if (prev && t - prev.end < 0.35 && iv >= 3 && iv <= 4) {
    const c = [];
    for (let k = Math.min(prev.midi, midi) + 1; k < Math.max(prev.midi, midi); k++) if (inMode(k)) c.push(k);
    if (c.length === 1 && r() < 0.5) return { m: c[0], gd: clamp(0.25 * d, 0.05, 0.085) };
  }
  if (r() < orn) {
    const dir = r() < 0.62 ? 1 : -1;
    for (let k = midi + dir; Math.abs(k - midi) <= 2; k += dir) if (inMode(k)) return { m: k, gd: clamp(0.2 * d, 0.04, 0.065) };
  }
  return null;
}

/* ---------------- hand drums: frame drum, slap, tambourine, finger taps, bodhrán tone (one noise source) ---------------- */
/** kit: { kickF0, kickF1, kickDecay, slapBP, slapDecay, jingle, lp }
 *  kick = the frame drum's "dum": sine kickF0 (100–120) → kickF1 (58–68) Hz (τ 35 ms), 0.45v, attack τ 2.5 ms (a palm, not a
 *         beater), decay τ kickDecay (.16–.24 s) + its 2nd membrane mode (sine 1.59 f, .22v, τ 60 ms) + the palm's thud
 *         (noise → BP 220 Hz, Q .8, .14v, τ 22 ms)
 *  snare = the slap ("tek"): noise → BP slapBP (1.3–1.8 kHz, Q .8), .62v, τ slapDecay (40–60 ms; ghost 25 ms) + the skin
 *         (triangle 330 → 280 Hz, .24v, τ 30 ms), pan −.08, reverb .22
 *  hat = tambourine jingles: noise → BP 7.2 kHz (Q 2.2) + BP 10.4 kHz (Q 2.8), .15v, rattling in 3 bursts 0 / 13 / 29 ms
 *         (1, .6, .35), τ jingle (50–80 ms); open = a shake, 5 bursts over 90 ms, τ .16 s; pan +.25, reverb .1
 *  rim = a finger "ka" on the frame: triangle 880 Hz .45 + noise BP 3 kHz (Q 1.4) .55, .34v, τ 11 ms, pan −.22, reverb .2
 *  shaker = the tambourine shaken softly: 4 bursts over 70 ms, .09v, τ 90 ms
 *  tom = the bodhrán's tone, bent by the hand: sine 150 → 112 Hz (τ 60 ms), .32v, τ .2 s, pan −.12, reverb .15 */
export function createFrameKit(ac, out, send, kit, { registry, tag, t0 = ac.currentTime }) {
  const L = LEVELS.kit * MED.kit, nodes = [], N = x => (nodes.push(x), x);
  const src = N(ac.createBufferSource());
  src.buffer = noise(ac, 'white'); src.loop = true;
  const bus = N(filter(ac, 'lowpass', kit.lp, 0.5));
  bus.connect(out);
  const kOsc = N(osc(ac, 'sine', kit.kickF1)), kAmp = N(gain(ac, 0)), kOsc2 = N(osc(ac, 'sine', 1.59 * kit.kickF1)), kAmp2 = N(gain(ac, 0));
  const kBP = N(filter(ac, 'bandpass', 220, 0.8)), kThud = N(gain(ac, 0));
  kOsc.connect(kAmp); kAmp.connect(bus); kOsc2.connect(kAmp2); kAmp2.connect(bus); src.connect(kBP); kBP.connect(kThud); kThud.connect(bus);
  const sBP = N(filter(ac, 'bandpass', kit.slapBP, 0.8)), sAmp = N(gain(ac, 0)), sTri = N(osc(ac, 'triangle', 280)), sTone = N(gain(ac, 0));
  const sPan = N(panner(ac, -0.08)), sSend = N(gain(ac, 0.22));
  src.connect(sBP); sBP.connect(sAmp); sAmp.connect(sPan); sTri.connect(sTone); sTone.connect(sPan); sPan.connect(bus); sPan.connect(sSend); sSend.connect(send);
  const j1 = N(filter(ac, 'bandpass', 7200, 2.2)), j2 = N(filter(ac, 'bandpass', 10400, 2.8)), jAmp = N(gain(ac, 0)), jPan = N(panner(ac, 0.25)), jSend = N(gain(ac, 0.1));
  src.connect(j1); src.connect(j2); j1.connect(jAmp); j2.connect(jAmp); jAmp.connect(jPan); jPan.connect(bus); jPan.connect(jSend); jSend.connect(send);
  const rTri = N(osc(ac, 'triangle', 880)), rg1 = N(gain(ac, 0.45)), rBP = N(filter(ac, 'bandpass', 3000, 1.4)), rg2 = N(gain(ac, 0.55));
  const rAmp = N(gain(ac, 0)), rPan = N(panner(ac, -0.22)), rSend = N(gain(ac, 0.2));
  rTri.connect(rg1); src.connect(rBP); rBP.connect(rg2); rg1.connect(rAmp); rg2.connect(rAmp); rAmp.connect(rPan); rPan.connect(bus); rPan.connect(rSend); rSend.connect(send);
  const tOsc = N(osc(ac, 'sine', 112)), tAmp = N(gain(ac, 0)), tPan = N(panner(ac, -0.12)), tSend = N(gain(ac, 0.15));
  tOsc.connect(tAmp); tAmp.connect(tPan); tPan.connect(bus); tPan.connect(tSend); tSend.connect(send);
  const p = persistent(registry, tag, [src, kOsc, kOsc2, sTri, rTri, tOsc], nodes, t0, 0.7);
  const hit = (param, peak, t, atk, rel, tau) => { param.setTargetAtTime(peak, t, atk); param.setTargetAtTime(0, t + rel, tau); };
  const jingle = (t, A, bursts, tau) => bursts.forEach(([d, w], k) => hit(jAmp.gain, A * w, t + d, 0.0006, 0.002, k === bursts.length - 1 ? tau : 0.012));
  return {
    kick(t, v) {
      kOsc.frequency.setValueAtTime(kit.kickF0, t); kOsc.frequency.setTargetAtTime(kit.kickF1, t, 0.035);
      kOsc2.frequency.setValueAtTime(1.59 * kit.kickF0, t); kOsc2.frequency.setTargetAtTime(1.59 * kit.kickF1, t, 0.035);
      hit(kAmp.gain, 0.45 * v * L, t, 0.0025, 0.01, kit.kickDecay);
      hit(kAmp2.gain, 0.22 * v * L, t, 0.002, 0.006, 0.06);
      hit(kThud.gain, 0.14 * v * L, t, 0.002, 0.004, 0.022);
    },
    snare(t, v, ghost = false) {
      sTri.frequency.setValueAtTime(330, t); sTri.frequency.setTargetAtTime(280, t, 0.03);
      hit(sAmp.gain, 0.62 * v * L, t, 0.0008, 0.003, ghost ? 0.025 : kit.slapDecay);
      hit(sTone.gain, 0.24 * v * L, t, 0.001, 0.004, 0.03);
    },
    hat(t, v, open = false) {
      if (open) jingle(t, 0.15 * v * L, [[0, 1], [0.022, 0.7], [0.045, 0.8], [0.068, 0.55], [0.09, 0.5]], 0.16);
      else jingle(t, 0.15 * v * L, [[0, 1], [0.013, 0.6], [0.029, 0.35]], kit.jingle);
    },
    rim(t, v) { hit(rAmp.gain, 0.34 * v * L, t, 0.0006, 0.002, 0.011); },
    shaker(t, v) { jingle(t, 0.09 * v * L, [[0, 0.6], [0.024, 1], [0.047, 0.8], [0.07, 0.6]], 0.09); },
    tom(t, v) {
      tOsc.frequency.setValueAtTime(150, t); tOsc.frequency.setTargetAtTime(112, t, 0.06);
      hit(tAmp.gain, 0.32 * v * L, t, 0.0015, 0.006, 0.2);
    },
    dispose(t) { p.dispose(t); },
  };
}
